import {
  fetchAnalyticsTimeSeries,
  buildSyntheticSeries,
  extractSelectedModels,
  parseSeriesDate,
  windowSeriesByRange,
  getActiveBrand,
  type TimeSeriesPoint,
  type SyntheticSeriesPoint,
} from "./analytics-overview-api";
import { fetchDeepSignalSeries, type SeriesIncompleteRun, type SeriesRebaselineMark } from "./deep-signal-series-api";
import { holdLastCaptured } from "./series-helpers";

export type TimeRange = "baseline" | "full";

export interface ReferralDataPoint {
  date: string;
  timestamp: number;
  [modelKey: string]: number | string;
}

export interface ReferralConversationsData {
  timeSeriesData: ReferralDataPoint[];
  events: any[];
  /** Plotted runs flagged incomplete (Deep Signal source only). */
  incompleteRuns?: SeriesIncompleteRun[];
  /** Series rebaselines between plotted reads (Deep Signal source only). */
  rebaselineMarks?: SeriesRebaselineMark[];
  selectedModels: string[];
  isSynthetic?: boolean;
}

function toDataPoint(p: TimeSeriesPoint | SyntheticSeriesPoint, idx: number, models: string[]): ReferralDataPoint {
  const base = (p as any).overall || 0;
  const parsed = parseSeriesDate(p.date);
  const result: any = { date: p.date, timestamp: Number.isNaN(parsed) ? idx : parsed };
  models.forEach(k => { result[k] = (p as any)[k] ?? base; });
  result.overall = base;          // composite, for the headline (not a plotted line)
  return result;
}

export async function fetchReferralConversationsData(
  timeRange: TimeRange,
  antennaData?: any,
  selectedModels?: string[],
): Promise<ReferralConversationsData> {
  // Primary source: Deep Signal runs. Mention Rate is the top of the Deep Signal
  // funnel (mention_rate), measured per run, per model.
  try {
    const ds = await fetchDeepSignalSeries(getActiveBrand());
    if (ds.runCount > 0) {
      const { series, models } = holdLastCaptured(ds.mention, ds.selectedModels);
      return {
        timeSeriesData: windowSeriesByRange(series, timeRange),
        events:         ds.publicationEvents,
        incompleteRuns: ds.incompleteRuns,
        rebaselineMarks: ds.rebaselineMarks,
        selectedModels: models,
        isSynthetic:    false,
      };
    }
  } catch { /* fall through to monitoring */ }

  try {
    // Fallback: Brand Monitoring snapshots. Fetch the full 90d window and slice
    // client-side (below) so every chart + the overview share ONE de-dup key
    // (brand|90), keeping the write-on-read endpoint to a single hit per load
    // regardless of time-range clicks.
    const ts = await fetchAnalyticsTimeSeries(undefined, 90);
    const windowed = windowSeriesByRange(ts.referral_series, timeRange);
    if (windowed.length > 0) {
      return {
        timeSeriesData: windowed.map((p, i) => toDataPoint(p, i, ts.selected_models)),
        events:         [],
        selectedModels: ts.selected_models,
        isSynthetic:    false,
      };
    }
  } catch { /* fall through */ }

  const models    = extractSelectedModels(antennaData, selectedModels);
  const synthetic = buildSyntheticSeries(antennaData, "referral", models);
  return {
    timeSeriesData: synthetic.map((p, i) => toDataPoint(p, i, models)),
    events:         [],
    selectedModels: models,
    isSynthetic:    true,
  };
}